import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { Box, Drawer, Grid, TextField, Button, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';

// Custom Components
import { FilesTable } from '../';

// Utils
import messages from '../../static/messages';

// Services
import resourceServices from '../../services/resourceServices';

// Styles
import { button } from '../../static/theme-styles';

// External variables
const required_fields = ['description', 'price'];

const ScopeOfWorkDrawer = (props) => {
  const { currentItem, setCurrentItem, createItem, updateItem } = props;
  const [errors, setErrors] = useState({});
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Action functions
  const resetErrors = () => {
    setErrors({});
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  };

  // Handler functions
  const handleClose = () => props.onCloseDrawer(resetErrors);

  const handleChange = (e) => {
    const key = e.target.name;
    const val = e.target.value;

    setCurrentItem((prevState) => ({
      ...prevState,
      [key]: val,
      isEdited: true,
    }));
  };

  const handleValidate = () => {
    resetErrors();

    required_fields.forEach((field) => {
      if (currentItem[field] === '' || currentItem[field] === null) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
      }
    });
  };

  // Async functions
  const onAddNewFile = async (e) => {
    const file = e.target.files[0];

    if (!currentItem.resources)
      setCurrentItem((prevState) => ({
        ...prevState,
        resources: [],
      }));

    try {
      const response = await resourceServices.upload(file, 'scope-of-work');

      if (response.status === 201) {
        setCurrentItem((prevState) => ({
          ...prevState,
          resources: [
            ...prevState.resources,
            {
              id: response.data.id,
            },
          ],
          isEdited: true,
        }));
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  }, []);

  useEffect(() => {
    if (currentItem?.isEdited) {
      !Object.values(errors).includes(true) && (props.isNewItem ? createItem() : updateItem());
    }
  }, [errors]);

  return (
    <Drawer 
      anchor="right" 
      open={props.drawer} 
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 400,
          maxWidth: '100%',
        }
      }}
    >
      <Box sx={{ p: isMobile ? 1.5 : 3 }}>
        {props.currentItem && (
          <Grid container spacing={isMobile ? 1.5 : 2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant={isMobile ? 'h6' : 'h4'} gutterBottom>
                  {props.isNewItem ? 'Add New Item' : 'Update Item'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                type="number"
                name="price"
                label="Price"
                defaultValue={currentItem.price}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                onChange={handleChange}
                error={errors.price}
                helperText={errors.price && messages.errors.required_field}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                name="description"
                label="Description"
                multiline
                rows={isMobile ? 3 : 4}
                defaultValue={currentItem.description}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                onChange={handleChange}
                error={errors.description}
                helperText={errors.description && messages.errors.required_field}
                InputProps={{
                  sx: { fontSize: isMobile ? '0.85rem' : 'inherit' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 1 }}>
                Resources
              </Typography>

              {currentItem?.resources?.length > 0 && (
                <FilesTable {...{ files: currentItem?.resources, setCurrentItem }} />
              )}

              {!currentItem?.resources?.length && (
                <Typography variant="body2" sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : 'inherit' }}>
                  No Files
                </Typography>
              )}

              <Button 
                variant="contained" 
                component="label" 
                size={isMobile ? 'small' : 'medium'}
                sx={{ ...button('secondary'), mt: 1 }}
              >
                Upload
                <input hidden accept="image/*" multiple type="file" onChange={onAddNewFile} />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0
              }}>
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ ...button('primary', 'secondary'), mr: isMobile ? 0 : 3 }}
                  disabled={!currentItem.isEdited}
                  onClick={handleValidate}
                  fullWidth={isMobile}
                >
                  {props.isNewItem ? 'Add New Item' : 'Update Item'}
                </Button>

                <Button 
                  variant="contained" 
                  onClick={handleClose} 
                  size={isMobile ? 'small' : 'medium'}
                  sx={button('secondary')}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default ScopeOfWorkDrawer;
